import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { useGetLatestResults } from '../services/module1Service';
import { useToast } from '../context/ToastContext';

interface LoadLatestResultsButtonProps {
  onResultsLoaded: (results: any) => void;
  disabled?: boolean;
  clientId?: string;
  workflowId?: string;
  hasResults?: boolean;
}

const LoadLatestResultsButton: React.FC<LoadLatestResultsButtonProps> = ({
  onResultsLoaded,
  disabled = false,
  clientId,
  workflowId,
  hasResults = false
}) => {
  const getLatestResults = useGetLatestResults();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Extract the results property if nested
  const extractRawResults = (data: any): any => {
    // Case 1: If data already has topics array, return it directly
    if (data.topics && Array.isArray(data.topics)) {
      return data;
    }
    
    // Case 2: If data has results with topics, extract them
    if (data.results && data.results.topics && Array.isArray(data.results.topics)) {
      return {
        topics: data.results.topics,
        missing_themes: data.results.missing_themes || []
      };
    }
    
    // Case 3: If data has nested content with topics, extract them
    if (data.results?.content?.topics && Array.isArray(data.results.content.topics)) {
      return {
        topics: data.results.content.topics,
        missing_themes: data.results.content.missing_themes || []
      };
    }
    
    // No transformations applied, just ensure proper structure
    return data;
  };

  const handleLoadLatestResults = async () => {
    setLoading(true);
    try {
      // Use the updated getLatestResults method that filters by client and workflow
      console.log(`Loading latest results for client: ${clientId}, workflow: ${workflowId}`);
      
      const results = await getLatestResults.mutateAsync({
        clientId,
        workflowId
      });
      
      if (results) {
        // Extract only what we need, with NO transformations
        const processedResults = extractRawResults(results);
        
        // Include any loaded keywords in the session storage for reuse
        if (results.originalKeywords && Array.isArray(results.originalKeywords)) {
          try {
            sessionStorage.setItem('latest_keywords', JSON.stringify(results.originalKeywords));
            console.log(`Stored ${results.originalKeywords.length} original keywords in session storage`);
          } catch (storageError) {
            console.warn('Could not store keywords in session storage:', storageError);
          }
        }
        
        showToast('Latest results loaded successfully', 'success');
        onResultsLoaded(processedResults);
      } else {
        const specificMessage = clientId && workflowId 
          ? `No previous results found for this client and workflow`
          : 'No previous results found';
        showToast(specificMessage, 'warning');
      }
    } catch (error) {
      console.error('Error loading latest results:', error);
      const errorMessage = clientId && workflowId 
        ? `Failed to load latest results for client ${clientId} and workflow ${workflowId}`
        : 'Failed to load latest results';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      startIcon={loading ? <CircularProgress size={20} /> : <HistoryIcon />}
      onClick={handleLoadLatestResults}
      disabled={loading || disabled}
      size="medium"
      sx={hasResults ? {
        backgroundColor: 'success.dark',
        color: 'white',
        borderColor: 'success.main',
        '&:hover': {
          backgroundColor: 'success.light',
          borderColor: 'success.light',
        }
      } : undefined}
    >
      {loading ? 'Loading...' : 'Load Latest Results'}
    </Button>
  );
};

export default LoadLatestResultsButton;
